import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  Shield, 
  Eye, 
  EyeOff, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Lock,
  Info,
  Clock
} from 'lucide-react';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 25px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 10px 0;
  color: #ffd700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
`;

const TicketInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const TicketClass = styled.div<{ class: string }>`
  background: ${props => {
    switch (props.class) {
      case 'first': return 'linear-gradient(45deg, #ffd700, #ffed4e)';
      case 'second': return 'linear-gradient(45deg, #c0c0c0, #e5e5e5)';
      case 'third': return 'linear-gradient(45deg, #cd7f32, #daa520)';
      case 'premium': return 'linear-gradient(45deg, #9c27b0, #e1bee7)';
      case 'vip': return 'linear-gradient(45deg, #ff5722, #ff8a65)';
      default: return 'linear-gradient(45deg, #2196f3, #64b5f6)';
    }
  }};
  color: ${props => props.class === 'second' ? '#333' : 'white'};
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  display: inline-block;
  margin-bottom: 15px;
`;

const TicketAmount = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #4caf50;
  margin-bottom: 10px;
`;

const TicketExpiry = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PinSection = styled.div`
  margin-bottom: 30px;
`;

const PinLabel = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: white;
  text-align: center;
`;

const PinInputContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 20px;
`;

const PinInput = styled.input<{ hasError: boolean; isFocused: boolean }>`
  width: 60px;
  height: 60px;
  background: ${props => props.hasError ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 255, 255, 0.1)'};
  border: 2px solid ${props => {
    if (props.hasError) return '#f44336';
    if (props.isFocused) return '#4caf50';
    return 'rgba(255, 255, 255, 0.3)';
  }};
  border-radius: 15px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4caf50;
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const PinToggle = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 20px;
  color: #f44336;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 20px;
  color: #4caf50;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.9rem;
`;

const AttemptsInfo = styled.div`
  text-align: center;
  margin-bottom: 20px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' | 'danger' }>`
  background: ${props => {
    switch (props.variant) {
      case 'primary': return 'linear-gradient(45deg, #4caf50, #8bc34a)';
      case 'secondary': return 'rgba(255, 255, 255, 0.1)';
      case 'danger': return 'rgba(244, 67, 54, 0.8)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'primary': return '#4caf50';
      case 'secondary': return 'rgba(255, 255, 255, 0.3)';
      case 'danger': return '#f44336';
      default: return 'rgba(255, 255, 255, 0.3)';
    }
  }};
  color: white;
  padding: 15px 30px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(76, 175, 80, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

interface PinValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (ticket: any) => void;
  ticket: any;
  maxAttempts?: number;
}

const PinValidationModal: React.FC<PinValidationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  ticket,
  maxAttempts = 3
}) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [showPin, setShowPin] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState<Date | null>(null);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (isOpen) {
      setPin(['', '', '', '']);
      setAttempts(0);
      setError('');
      setSuccess(false);
      setIsLocked(false);
      setLockoutTime(null);
      // Focus first input
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (lockoutTime) {
      const timer = setInterval(() => {
        const now = new Date();
        if (now >= lockoutTime) {
          setIsLocked(false);
          setLockoutTime(null);
          setAttempts(0);
        }
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [lockoutTime]);

  const handlePinChange = (index: number, value: string) => {
    if (isLocked) return;
    
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newPin = [...pin];
      newPin[index] = value;
      setPin(newPin);
      setError('');
      
      // Auto-focus next input
      if (value && index < 3) {
        setTimeout(() => {
          if (inputRefs.current[index + 1]) {
            inputRefs.current[index + 1]?.focus();
          }
        }, 100);
      }
      
      // Auto-validate when all digits are entered
      if (newPin.every(digit => digit !== '') && index === 3) {
        handleValidatePin();
      }
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) prevInput.focus();
    }
  };

  const handleValidatePin = async () => {
    if (isLocked || pin.some(digit => digit === '')) return;
    
    setIsValidating(true);
    setError('');
    
    // Simulate API validation
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const enteredPin = pin.join('');
    const isValid = enteredPin === ticket.pin;
    
    if (isValid) {
      setSuccess(true);
      setIsValidating(false);
      
      // Auto-close after success
      setTimeout(() => {
        onSuccess(ticket);
        onClose();
      }, 1500);
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setIsValidating(false);
      
      if (newAttempts >= maxAttempts) {
        setIsLocked(true);
        setLockoutTime(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes lockout
        setError(`Too many failed attempts. Account locked for 5 minutes.`);
      } else {
        setError(`Invalid PIN. ${maxAttempts - newAttempts} attempts remaining.`);
        setPin(['', '', '', '']);
        // Focus first input
        setTimeout(() => {
          if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
          }
        }, 100);
      }
    }
  };

  const handleClose = () => {
    if (!isValidating && !success) {
      onClose();
    }
  };

  const getRemainingLockoutTime = () => {
    if (!lockoutTime) return 0;
    const now = new Date();
    const remaining = Math.max(0, lockoutTime.getTime() - now.getTime());
    return Math.ceil(remaining / 1000);
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <XCircle size={20} />
        </CloseButton>

        <Header>
          <Title>
            <Shield size={32} />
            Enter Your PIN
          </Title>
          <Subtitle>
            Please enter the 4-digit PIN associated with your ticket to continue
          </Subtitle>
        </Header>

        <TicketInfo>
          <TicketClass class={ticket.ticketClass}>
            {ticket.ticketClass.toUpperCase()} CLASS
          </TicketClass>
          <TicketAmount>KES {ticket.amount.toLocaleString()}</TicketAmount>
          <TicketExpiry>
            <Clock size={16} />
            Expires: {ticket.expiryDate.toLocaleDateString()}
          </TicketExpiry>
        </TicketInfo>

        <PinSection>
          <PinLabel>Enter your 4-digit PIN</PinLabel>
          
          <PinInputContainer>
            {pin.map((digit, index) => (
              <PinInput
                key={index}
                ref={(el) => inputRefs.current[index] = el}
                type={showPin ? 'text' : 'password'}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handlePinKeyDown(index, e)}
                maxLength={1}
                placeholder="0"
                hasError={!!error}
                isFocused={document.activeElement === inputRefs.current[index]}
                disabled={isLocked}
              />
            ))}
          </PinInputContainer>
          
          <PinToggle onClick={() => setShowPin(!showPin)}>
            {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
            {showPin ? ' Hide PIN' : ' Show PIN'}
          </PinToggle>
        </PinSection>

        {error && (
          <ErrorMessage>
            <AlertTriangle size={16} />
            {error}
          </ErrorMessage>
        )}

        {success && (
          <SuccessMessage>
            <CheckCircle size={16} />
            PIN validated successfully! Redirecting...
          </SuccessMessage>
        )}

        {!isLocked && !success && (
          <AttemptsInfo>
            <Info size={16} />
            {maxAttempts - attempts} attempts remaining
          </AttemptsInfo>
        )}

        {isLocked && (
          <AttemptsInfo>
            <Lock size={16} />
            Account locked for {getRemainingLockoutTime()} seconds
          </AttemptsInfo>
        )}

        <ActionButtons>
          {!success && (
            <>
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isValidating || isLocked}
              >
                Cancel
              </Button>
              
              <Button
                variant="primary"
                onClick={handleValidatePin}
                disabled={pin.some(digit => digit === '') || isValidating || isLocked}
              >
                {isValidating ? (
                  <>
                    <LoadingSpinner />
                    Validating...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Validate PIN
                  </>
                )}
              </Button>
            </>
          )}
        </ActionButtons>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PinValidationModal;
